import React, { useLayoutEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { Footer } from '../../components'
import logo from '../../assets/logo-black.svg'

import './notFoundPage.scss'

function Ticket({ x, y, rotate = 0, missing = false }) {
  return (
    <g transform={`translate(${x} ${y}) rotate(${rotate} 78 52)`}>
      <rect
        width="156"
        height="104"
        rx="14"
        fill={missing ? 'rgba(90, 182, 248, 0.14)' : '#ffffff'}
        stroke={missing ? '#5ab6f8' : '#d3e6f5'}
        strokeWidth="2"
        strokeDasharray={missing ? '7 6' : undefined}
      />
      {missing ? (
        <g fill="none" stroke="#5ab6f8" strokeWidth="2" strokeLinecap="round">
          <path d="M78 38v18" />
          <path d="M78 66v2" />
        </g>
      ) : (
        <>
          <rect width="156" height="8" rx="4" fill="#5ab6f8" />
          <rect x="18" y="28" width="70" height="8" rx="4" fill="#051b35" />
          <rect x="18" y="44" width="48" height="6" rx="3" fill="#b7cfe0" />
          <rect x="18" y="58" width="36" height="6" rx="3" fill="#d5e4ef" />
          <line
            x1="112"
            y1="20"
            x2="112"
            y2="88"
            stroke="#d3e6f5"
            strokeDasharray="4 4"
          />
          <rect x="122" y="34" width="18" height="6" rx="2" fill="#5ab6f8" />
          <rect x="122" y="46" width="18" height="6" rx="2" fill="#3e8bf7" />
          <rect
            x="122"
            y="58"
            width="18"
            height="6"
            rx="2"
            fill="#051b35"
            opacity="0.28"
          />
        </>
      )}
    </g>
  )
}

function SeatGrid({ x, y }) {
  const originX = Number(x)
  const originY = Number(y)
  const fills = ['#d7eefc', '#5ab6f8', '#8ecdf8', '#3e8bf7', '#bfe4fb']
  const cells = []

  for (let row = 0; row < 4; row += 1) {
    for (let col = 0; col < 5; col += 1) {
      const missing = row === 1 && col === 3
      cells.push(
        <rect
          key={`${row}-${col}`}
          x={originX + col * 22}
          y={originY + row * 22}
          width="16"
          height="16"
          rx="4"
          fill={missing ? 'none' : fills[(row * 2 + col) % fills.length]}
          stroke={missing ? '#5ab6f8' : 'none'}
          strokeWidth={missing ? 1.5 : 0}
          strokeDasharray={missing ? '3 2' : undefined}
        />
      )
    }
  }

  return <g>{cells}</g>
}

function MissingPageBanner() {
  return (
    <svg
      className="not-found__art"
      viewBox="0 0 1200 240"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id="blocktickets-404-sky" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#5ab6f8" stopOpacity="0.18" />
          <stop offset="55%" stopColor="#f6f8fa" stopOpacity="0" />
          <stop offset="100%" stopColor="#051b35" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <rect width="1200" height="240" fill="url(#blocktickets-404-sky)" />
      <Ticket x="96" y="68" rotate={-8} />
      <Ticket x="292" y="52" rotate={5} />
      <Ticket x="512" y="62" missing />
      <Ticket x="732" y="50" rotate={-4} />
      <SeatGrid x="960" y="72" />
    </svg>
  )
}

export default function NotFoundPage({
  title = "This page doesn't exist",
  message = 'The link may be out of date, or the tickets you are looking for are no longer available.',
}) {
  useLayoutEffect(() => {
    document.documentElement.classList.add('not-found-page')
    document.body.classList.add('not-found-page')

    return () => {
      document.documentElement.classList.remove('not-found-page')
      document.body.classList.remove('not-found-page')
    }
  }, [])

  return (
    <div className="not-found">
      <Helmet>
        <title>Page not found | Blocktickets</title>
        <meta name="robots" content="noindex" />
        <meta name="theme-color" content="#051b35" />
      </Helmet>
      <header className="not-found__header">
        <Link to="/" className="not-found__brand">
          <img src={logo} alt="blocktickets" />
        </Link>
      </header>
      <div className="not-found__banner">
        <MissingPageBanner />
      </div>
      <main className="not-found__main">
        <p className="not-found__code">404</p>
        <h1>{title}</h1>
        <p className="not-found__message">{message}</p>
        <div className="not-found__actions">
          <Link to="/browse" className="not-found__primary">
            Browse events
          </Link>
          <Link to="/" className="not-found__secondary">
            Go home
          </Link>
        </div>
      </main>
      <div className="not-found__footer">
        <Footer />
      </div>
    </div>
  )
}
